import { fileHref } from '../lib/fileAccess'
import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { shipTodayAuto } from '../lib/shipTime'
import { SECTIONS, ALL_ROWS, reviewSources, autoStatus } from '../lib/monthlyReview'
import html2pdf from 'html2pdf.js'
import { filePath } from '../lib/filePaths'
import { escHtml } from '../lib/escHtml'

export default function OfficerReview() {
  const { config } = useAuth()
  const [month, setMonth] = useState(() => shipTodayAuto().slice(0, 7))
  const [archives, setArchives] = useState([])
  const [showArchives, setShowArchives] = useState(false)
  useEffect(() => {
    if (!config?.id) return
    supabase.from('officer_monthly_reviews').select('month, signed_by, archived_at, archive_url').eq('client_id', config.id).not('archive_url', 'is', null).order('month', { ascending: false })
      .then(({ data }) => setArchives(data || []))
  }, [config?.id])
  const [auto, setAuto] = useState({})          // key -> { done, date }
  const [state, setState] = useState({})         // key -> { confirmed, note }
  const [sign, setSign] = useState({ by: '', role: '', at: null })
  const [rowId, setRowId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [expandedKey, setExpandedKey] = useState(null)
  const [archiving, setArchiving] = useState(false)

  useEffect(() => { if (config?.id) load() }, [config?.id, month]) // eslint-disable-line

  async function load() {
    setLoading(true); setMsg('')
    try {
      // saved review for this month
      const { data: rev } = await supabase.from('officer_monthly_reviews')
        .select('*').eq('client_id', config.id).eq('month', month).maybeSingle()
      setRowId(rev?.id || null)
      setState(rev?.rows || {})
      setSign({ by: rev?.signed_by || '', role: rev?.signed_role || '', at: rev?.signed_at || null })

      // derive auto status per row: the same reader + rules as the AI (reviewSources / autoStatus in lib/monthlyReview.js)
      const rowsByTable = {}
      await Promise.all(reviewSources(month).map(async src => {
        let q = supabase.from(src.table).select(src.select).limit(src.limit)
        if (!src.noClient) q = q.eq('client_id', config.id)
        if (src.eq) q = q.eq(src.eq.field, src.eq.value)
        if (src.range) q = q.gte(src.range.field, src.range.from).lt(src.range.field, src.range.to)
        if (src.order) { const [f, dir] = src.order.split('.'); q = q.order(f, { ascending: dir !== 'desc' }) }
        const { data, error } = await q
        rowsByTable[src.table] = error ? [] : (data || [])
      }))
      const detected = autoStatus(rowsByTable, month, shipTodayAuto())
      setAuto(detected)
    } catch (e) { setMsg('Load failed: ' + (e.message || 'unknown')) }
    finally { setLoading(false) }
  }

  function setNote(key, note) {
    setState(s => ({ ...s, [key]: { ...(s[key] || {}), note } }))
  }
  function toggleConfirm(key) {
    setState(s => ({ ...s, [key]: { ...(s[key] || {}), confirmed: !(s[key]?.confirmed) } }))
  }

  // a row counts as done if a module detected it OR the officer confirmed it
  const statusOf = key => {
    if (auto[key]?.done) return { done: true, date: auto[key].date, via: 'system', label: auto[key].label }
    if (state[key]?.confirmed) return { done: true, date: null, via: 'manual' }
    return { done: false }
  }

  const doneCount = ALL_ROWS.filter(r => statusOf(r.key).done).length

  async function save(withSignature) {
    if (!config?.id) return
    setSaving(true); setMsg('')
    try {
      const body = {
        client_id: config.id, month, rows: state,
      }
      if (withSignature) {
        if (!sign.by.trim()) { setMsg('Enter who is signing before signing off.'); setSaving(false); return }
        body.signed_by = sign.by.trim()
        body.signed_role = sign.role.trim() || null
        body.signed_at = new Date().toISOString()
      }
      let res
      if (rowId) res = await supabase.from('officer_monthly_reviews').update(body).eq('id', rowId).select().single()
      else res = await supabase.from('officer_monthly_reviews').insert(body).select().single()
      if (res.error) throw new Error(res.error.message)
      setRowId(res.data.id)
      // the database sets who signed (the login's name / rank), and when (sql/sec-fix.sql omr_sign_guard): show what it stored
      if (withSignature) setSign({ by: res.data.signed_by || '', role: res.data.signed_role || '', at: res.data.signed_at || body.signed_at })
      setMsg(withSignature ? 'Signed off for ' + month + '.' : 'Saved.')
    } catch (e) { setMsg('Save failed: ' + (e.message || 'unknown')) }
    finally { setSaving(false) }
  }

  function buildReviewHtml() {
    const NAVY = '#15294a', BLUE = '#2457d6', INK = '#26313c', MUTE = '#5b6878', LINE = '#e4e9f0', SOFT = '#f6f9fc', GREEN = '#1f6b3a'
    const FONT = "'Trebuchet MS','Segoe UI','Helvetica Neue',Arial,sans-serif"
    const vessel = escHtml((config && config.vessel_name) || 'Vessel')
    const wave = '<span style="display:inline-block;font-size:34px;font-weight:900;color:' + BLUE + ';line-height:1;letter-spacing:-3px;">\u2248</span>'

    const rowsHtml = SECTIONS.map(sec => {
      const body = sec.rows.map(r => {
        const st = statusOf(r.key)
        const badge = st.done
          ? '<span style="color:' + (st.via === 'system' ? GREEN : BLUE) + ';font-weight:700;font-size:10px;">' + (st.via === 'system' ? escHtml(String(st.label || 'Done').toUpperCase()) : 'CONFIRMED') + '</span>'
          : '<span style="color:' + MUTE + ';font-weight:700;font-size:10px;">PENDING</span>'
        const items = (auto[r.key]?.items || []).map(it => '<div style="font-size:10px;color:' + MUTE + ';padding-left:10px;">\u2022 ' + escHtml(it) + '</div>').join('')
        const note = state[r.key]?.note ? '<div style="font-size:10.5px;color:' + INK + ';font-style:italic;margin-top:2px;">' + escHtml(state[r.key].note) + '</div>' : ''
        return '<tr>' +
          '<td style="border:1px solid ' + LINE + ';padding:7px 10px;font-size:12px;color:' + INK + ';vertical-align:top;">' + r.label + note + items + '</td>' +
          '<td style="border:1px solid ' + LINE + ';padding:7px 10px;text-align:center;white-space:nowrap;vertical-align:top;">' + badge + '</td>' +
          '<td style="border:1px solid ' + LINE + ';padding:7px 10px;text-align:center;font-size:10.5px;color:' + MUTE + ';vertical-align:top;">' + escHtml(st.date || '\u2014') + '</td>' +
          '</tr>'
      }).join('')
      return '<tr><td colspan="3" style="background:' + NAVY + ';color:#fff;padding:7px 10px;font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;">' + sec.group + '</td></tr>' + body
    }).join('')

    const signBlock = sign.at
      ? '<div style="font-size:12px;color:' + INK + ';">Signed by <b>' + escHtml(sign.by) + '</b>' + (sign.role ? ', ' + escHtml(sign.role) : '') + ' \u00b7 ' + String(sign.at).slice(0,10) + '</div>'
      : '<div style="font-size:12px;color:' + MUTE + ';">Not yet signed</div>'

    return '<div style="width:960px;font-family:' + FONT + ';color:' + INK + ';background:#fff;">' +
      '<style>* { -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; }</style>' +
      '<div style="padding:18px 30px 14px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid ' + LINE + ';">' +
        '<div style="display:flex;align-items:center;gap:13px;">' + wave + '<span style="font-size:22px;font-weight:800;letter-spacing:.13em;color:' + NAVY + ';">HEYCAPTAIN</span></div>' +
        '<div style="font-size:10px;font-weight:700;letter-spacing:.17em;color:' + MUTE + ';text-transform:uppercase;">Safety Management System</div>' +
      '</div>' +
      '<div style="background:' + NAVY + ';padding:22px 30px;display:flex;justify-content:space-between;align-items:center;">' +
        '<div><div style="font-size:24px;font-weight:700;color:#fff;">OFFICER MONTHLY REVIEW</div><div style="font-size:12.5px;color:#aec3d6;margin-top:4px;">' + vessel + '</div></div>' +
        '<div style="border:1.5px solid ' + BLUE + ';border-radius:9px;padding:10px 18px;text-align:center;"><div style="font-size:9px;color:#aec3d6;letter-spacing:.14em;margin-bottom:3px;">MONTH</div><div style="font-size:15px;font-weight:700;color:#fff;">' + escHtml(month) + '</div></div>' +
      '</div>' +
      '<div style="padding:22px 30px 26px;">' +
        '<table style="width:100%;border-collapse:collapse;"><colgroup><col><col style="width:110px"><col style="width:100px"></colgroup>' + rowsHtml + '</table>' +
        '<div style="margin-top:22px;padding-top:14px;border-top:2px solid ' + NAVY + ';display:flex;justify-content:space-between;align-items:center;">' +
          signBlock +
          '<div style="font-size:11px;color:' + MUTE + ';">' + doneCount + ' / ' + ALL_ROWS.length + ' complete</div>' +
        '</div>' +
        '<div style="margin-top:18px;padding-top:11px;border-top:1px solid ' + LINE + ';display:flex;justify-content:space-between;font-size:8.5px;color:' + MUTE + ';">' +
          '<span style="font-weight:800;letter-spacing:.12em;color:' + NAVY + ';">HEYCAPTAIN</span>' +
          '<span>Hey Captain \u00b7 Superyacht Safety Management System</span>' +
        '</div>' +
      '</div></div>'
  }

  function exportPdf() {
    const el = document.createElement('div')
    el.innerHTML = buildReviewHtml()
    document.body.appendChild(el)
    const fname = ('Officer_Monthly_Review_' + month).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    const opt = { margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 }, pagebreak: { mode: ['legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el))
  }

  async function archivePdf() {
    if (!config?.id) return
    if (!rowId) { setMsg('Save or sign the review before archiving.'); return }
    setArchiving(true); setMsg('')
    try {
      const el = document.createElement('div')
      el.innerHTML = buildReviewHtml()
      document.body.appendChild(el)
      const opt = { margin: 8, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
      const blob = await html2pdf().set(opt).from(el).outputPdf('blob')
      document.body.removeChild(el)
      const path = filePath('officer_reviews', config.id, month + '.pdf')
      const { error: upErr } = await supabase.storage.from('crew-docs').upload(path, blob, { contentType: 'application/pdf', upsert: false })
      if (upErr) throw new Error(upErr.message)
      const { error: dbErr } = await supabase.from('officer_monthly_reviews').update({ archive_url: path, archived_at: new Date().toISOString() }).eq('id', rowId)
      if (dbErr) throw new Error(dbErr.message)
      setMsg('Archived. PDF saved to vessel records.')
    } catch (e) { setMsg('Archive failed: ' + (e.message || 'unknown')) }
    finally { setArchiving(false) }
  }

  const pill = (done, via) => ({
    display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
    padding: '3px 9px', borderRadius: 11, textTransform: 'uppercase',
    background: done ? (via === 'system' ? 'rgba(31,107,58,.12)' : 'rgba(27,79,216,.10)') : 'rgba(125,138,153,.12)',
    color: done ? (via === 'system' ? '#1f6b3a' : '#1B4FD8') : 'var(--muted)',
  })

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '4px 4px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{doneCount} / {ALL_ROWS.length} complete</span>
        {loading && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Checking modules...</span>}
        {sign.at && <span style={{ fontSize: 12, color: '#1f6b3a', fontWeight: 600 }}>Signed by {sign.by} on {String(sign.at).slice(0, 10)}</span>}
        <button onClick={() => setShowArchives(v => !v)} style={{ marginLeft: 'auto', border: '0.5px solid var(--border, #1f2630)', background: 'var(--card)', color: 'var(--muted)', fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 8, cursor: 'pointer' }}>{showArchives ? 'Hide archive' : 'Archived reviews (' + archives.length + ')'}</button>
      </div>

      {showArchives && (
        <div style={{ border: '0.5px solid var(--border, #1f2630)', borderRadius: 10, padding: 14, marginBottom: 16, background: 'var(--card)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Archived Monthly Reviews</div>
          {archives.length === 0 ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>No archived reviews yet.</div> : archives.map(a => (
            <div key={a.month} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '0.5px solid var(--border, #1f2630)' }}>
              <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, minWidth: 90 }}>{a.month}</span>
              <span style={{ fontSize: 12, color: 'var(--muted)', flex: 1 }}>{a.signed_by ? 'Signed by ' + a.signed_by : 'Archived'}{a.archived_at ? ' · ' + String(a.archived_at).slice(0,10) : ''}</span>
              <a href={fileHref(a.archive_url)} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--gold2)', textDecoration: 'none', fontWeight: 600 }}>Open PDF ↗</a>
            </div>
          ))}
        </div>
      )}

      {SECTIONS.map(sec => (
        <div key={sec.group} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--navy, #15294a)', borderBottom: '2px solid var(--navy, #15294a)', paddingBottom: 5, marginBottom: 8 }}>{sec.group}</div>
          {sec.rows.map(r => {
            const st = statusOf(r.key)
            return (
              <div key={r.key}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '9px 4px', borderBottom: '1px solid var(--line)' }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{r.label}</div>
                  <input
                    value={state[r.key]?.note || ''}
                    onChange={e => setNote(r.key, e.target.value)}
                    placeholder="Notes / exceptions"
                    style={{ marginTop: 4, width: '100%', maxWidth: 520, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--text)', fontSize: 12 }}
                  />
                </div>
                <div style={{ textAlign: 'right', minWidth: 150 }}>
                  <div style={pill(st.done, st.via)}>{st.done ? (st.via === 'system' ? (st.label || 'Done') : 'Confirmed') : 'Pending'}</div>
                  {auto[r.key]?.count > 0 && (
                    <button onClick={() => setExpandedKey(k => k === r.key ? null : r.key)}
                      style={{ display: 'block', marginLeft: 'auto', marginTop: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11 }}>
                      {auto[r.key].count} item{auto[r.key].count > 1 ? 's' : ''} {expandedKey === r.key ? '\u25be' : '\u25b8'}
                    </button>
                  )}
                  {!auto[r.key]?.count && st.date && <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>{st.date}</div>}
                  {!st.done && (
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!state[r.key]?.confirmed} onChange={() => toggleConfirm(r.key)} style={{ marginRight: 4 }} />
                      confirm
                    </label>
                  )}
                </div>
              </div>
              {expandedKey === r.key && auto[r.key]?.items && (
                <div style={{ gridColumn: '1 / -1', padding: '4px 0 10px 4px' }}>
                  {auto[r.key].items.map((it, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--text)', padding: '3px 10px', borderLeft: '2px solid var(--line)', marginLeft: 2 }}>{it}</div>
                  ))}
                </div>
              )}
              </div>
            )
          })}
        </div>
      ))}

      <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 16, marginTop: 20, background: 'var(--card)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Monthly Sign-Off</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <input value={sign.by} onChange={e => setSign(s => ({ ...s, by: e.target.value }))} placeholder="Signed by (name)"
            style={{ flex: 1, minWidth: 180, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg2, var(--card))', color: 'var(--text)', fontSize: 13 }} />
          <input value={sign.role} onChange={e => setSign(s => ({ ...s, role: e.target.value }))} placeholder="Rank (e.g. 1st Officer)"
            style={{ flex: 1, minWidth: 160, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg2, var(--card))', color: 'var(--text)', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" disabled={saving} onClick={() => save(false)}>Save draft</button>
          <button className="btn btn-primary" disabled={saving} onClick={() => save(true)}>Sign off month</button>
          <button className="btn" onClick={exportPdf}>Export PDF</button>
          <button className="btn" disabled={archiving} onClick={archivePdf}>{archiving ? 'Archiving...' : 'Save to Archive'}</button>
        </div>
        {msg && <div style={{ marginTop: 12, fontSize: 12, color: msg.includes('failed') ? '#b4313c' : 'var(--muted)' }}>{msg}</div>}
      </div>
    </div>
  )
}
